import { Download, X } from 'lucide-react';
import { useId, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { promptInstall, useInstallRoute, type InstallRoute } from './install';
import { InstallGuide } from './InstallAppItem';
import {
  currentDeviceClass,
  inviteFor,
  snoozeInvite,
  stopInvite,
  storedFacts,
  type DeviceClass,
} from './install-invite';

/**
 * The invite to install the app on a phone or a tablet (decision 110, #418): a band under the
 * header, in the new-version band's slot and look, which gives way to it (`updateShown`).
 *
 * «Installa» opens the browser's own dialog where there is one (Android, Chrome), else the 3-step
 * guide (iPhone, iPad). «Non ora» hides it for 30 days on this device. On iPhone and iPad, where
 * Safari cannot tell whether the app is installed, «È già installata» stops it for good.
 * A region with a name, not an alert: it invites, it does not interrupt.
 */
export function InstallInvite({
  updateShown,
  route: forcedRoute,
  device: forcedDevice,
}: {
  readonly updateShown: boolean;
  readonly route?: InstallRoute;
  readonly device?: DeviceClass;
}) {
  const { t } = useTranslation('shell');
  const detected = useInstallRoute();
  const route = forcedRoute ?? detected;
  const [device] = useState<DeviceClass>(() => forcedDevice ?? currentDeviceClass());
  const [hidden, setHidden] = useState(false);
  const [guideOpen, setGuideOpen] = useState(false);
  const installRef = useRef<HTMLButtonElement | null>(null);
  const labelId = useId();

  const invite = hidden
    ? null
    : inviteFor({ route, device, updateShown, now: Date.now(), ...storedFacts() });
  if (invite === null && !guideOpen) {
    return null;
  }

  function notNow() {
    snoozeInvite();
    setHidden(true);
  }

  function alreadyInstalled() {
    stopInvite();
    setHidden(true);
  }

  return (
    <>
      {invite !== null ? (
        <section
          aria-labelledby={labelId}
          className="border-border bg-accent text-accent-foreground flex flex-wrap items-center justify-between gap-x-4 gap-y-2 border-b px-[clamp(16px,2.4vw,24px)] py-2 text-sm print:hidden"
        >
          <p id={labelId} className="m-0 font-semibold">
            {t(device === 'tablet' ? 'installInvite.textTablet' : 'installInvite.textPhone')}
          </p>
          <div className="flex flex-wrap items-center gap-1">
            <Button
              ref={installRef}
              type="button"
              variant="outline"
              className="min-h-11"
              onClick={() => {
                if (invite === 'prompt') {
                  setHidden(true);
                  void promptInstall();
                } else {
                  setGuideOpen(true);
                }
              }}
            >
              <Download aria-hidden="true" />
              {t('installInvite.install')}
            </Button>
            {invite === 'ios-guide' ? (
              <Button type="button" variant="ghost" className="min-h-11" onClick={alreadyInstalled}>
                {t('installInvite.already')}
              </Button>
            ) : null}
            <Button type="button" variant="ghost" className="min-h-11" onClick={notNow}>
              <X aria-hidden="true" />
              {t('installInvite.notNow')}
            </Button>
          </div>
        </section>
      ) : null}
      {invite === 'ios-guide' || guideOpen ? (
        <InstallGuide
          platform="ios"
          open={guideOpen}
          onOpenChange={setGuideOpen}
          returnFocusTo={installRef}
        />
      ) : null}
    </>
  );
}
